import { QuoteOfferingGroup } from '@monetize/types/app';
import { BsTrash } from 'react-icons/bs';
import { MCustomIconButton } from '../../../../../components/Monetize';
import { QuoteOfferingGroupCustomFieldsPopover } from './QuoteOfferingGroupCustomFieldsPopover';
import {
  getGroupToggleLabel,
  GroupHeaderAccountField,
  GroupHeaderDivider,
  QuoteOfferingGroupHeaderShell,
} from './QuoteOfferingGroupHeaderShell';

interface QuoteOfferingGroupHeaderProps {
  group: QuoteOfferingGroup;
  /** Unique per rendered container — see QuoteOfferingGroupContainer */
  testIdKey: string;
  /**
   * Table section this container sits in, when the table has more than one.
   * Qualifies the collapse control's accessible name, which would otherwise
   * repeat for a group holding both existing and newly added offerings.
   */
  sectionLabel?: string;
  /** 1-based position in the quote's group list — shown as "Group N" */
  groupNumber: number;
  offeringCount: number;
  /** Pre-formatted sum of the group's offerings, ramp segments included */
  subtotal: string;
  isOpen: boolean;
  onToggle: () => void;
  canDelete: boolean;
  isActionPending: boolean;
  onDelete: () => void;
}

/**
 * Header strip for a quote offering group container — identifies the group by
 * its shipping/billing accounts, carries its subtotal, and hosts the collapse
 * and delete actions. Editing a group's accounts, contacts and membership
 * happens in the Manage Groups modal.
 */
export const QuoteOfferingGroupHeader = ({
  group,
  testIdKey,
  sectionLabel,
  groupNumber,
  offeringCount,
  subtotal,
  isOpen,
  onToggle,
  canDelete,
  isActionPending,
  onDelete,
}: QuoteOfferingGroupHeaderProps) => {
  const groupLabel = `Group ${groupNumber}`;
  const testId = `quote-offering-group-header-${testIdKey}`;

  return (
    <QuoteOfferingGroupHeaderShell
      title={groupLabel}
      offeringCount={offeringCount}
      subtotal={subtotal}
      isOpen={isOpen}
      onToggle={onToggle}
      toggleLabel={getGroupToggleLabel(groupLabel, sectionLabel)}
      testId={testId}
      fields={
        <>
          <GroupHeaderAccountField
            label="Ship To"
            accountId={group.shippingAccount.id}
            accountName={group.shippingAccount.accountName}
          />
          <GroupHeaderDivider />
          <GroupHeaderAccountField
            label="Bill To"
            accountId={group.billingAccount.id}
            accountName={group.billingAccount.accountName}
          />
        </>
      }
      trailing={
        <QuoteOfferingGroupCustomFieldsPopover
          customFields={group.customFields}
          groupLabel={groupLabel}
          testId={testId}
        />
      }
      action={
        canDelete && (
          <MCustomIconButton
            btnSize={4}
            containerSize={6}
            icon={BsTrash}
            aria-label={`Delete ${groupLabel}`}
            onClick={onDelete}
            isDisabled={isActionPending}
          />
        )
      }
    />
  );
};
